'use client';

import React, { useState, useEffect } from 'react';
import classnames from 'classnames';

import Box from '../../Box/Box';
import Iconography from '../../Iconography/Iconography';
import Typography from '../../Typography/Typography';

import styles from './RadioButton.module.scss';

type Theme = 'dark' | 'light';
type Direction = 'ltr' | 'rtl';

interface RadioOption {
  value: string;
  label: string;
  description?: string;
  default?: boolean;
  disabled?: boolean;
}

interface RegisterFunction {
  (name: string): {
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
    name: string;
    ref: React.Ref<HTMLInputElement>;
  };
}

export interface RadioButtonProps {
  theme?: Theme;
  index: number;
  name?: string;
  register?: RegisterFunction;
  option: RadioOption;
  groupCount?: number;
  direction?: Direction;
  isChecked?: boolean;
  isDisabled?: boolean;
  onFocus?: (e: React.FocusEvent<HTMLDivElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLDivElement>) => void;
  onClick?: () => void;
  dataTestId?: string;
  gaComponent?: string;
  logEvents?: boolean;
}

const RadioButton: React.FC<RadioButtonProps> = ({
  theme = 'dark',
  index,
  name,
  register = undefined,
  option,
  groupCount = 1,
  direction = 'ltr',
  isChecked = false,
  isDisabled = false,
  onFocus = () => {},
  onBlur = () => {},
  onClick = () => {},
  dataTestId = '',
  gaComponent = '',
  logEvents = true,
}) => {
  const [checked, setChecked] = useState(isChecked);

  useEffect(() => {
    setChecked(isChecked);
  }, [isChecked]);

  const handleClick = () => {
    if (checked || isDisabled) return;

    if (typeof window !== 'undefined' && logEvents) {
      const dataLayerMap: Record<string, unknown> = {
        event: 'interaction',
        interaction__type: 'radioButton',
        interaction__component: gaComponent,
      };
      if (option?.label) {
        dataLayerMap.interaction__text = option.label;
      }

      (window as unknown as { dataLayer?: unknown[] })?.dataLayer?.push(dataLayerMap);
    }

    onClick();
  };

  return (
    <div className={styles.radioButtonWrapper}>
      <input
        id={`radioButton_${index}`}
        className={styles.hiddenRadio}
        type="radio"
        value={option.value}
        checked={checked}
        {...(register && name ? register(name) : { name })}
        tabIndex={-1}
        readOnly
      />
      <label htmlFor={`radioButton_${index}`}>
        <div
          key={index}
          className={classnames(styles.radioButton, styles[`theme_${theme}`], {
            [styles.disabled]: isDisabled,
          })}
          onClick={handleClick}
          data-testid={dataTestId}
          dir={direction}
        >
          <div
            className={classnames(styles.radioButtonIconWrapper, {
              [styles.checked]: checked,
              radioButtonFocusTarget: !isDisabled,
            })}
            tabIndex={-1}
            onFocus={onFocus}
            onBlur={onBlur}
            role="radio"
            aria-checked={checked}
            aria-label={option?.label}
            aria-describedby={`description-${index}`}
            aria-keyshortcuts="Enter Space"
          >
            <span id={`description-${index}`} hidden>
              {option.description} ({index + 1}/{groupCount})
            </span>
            {checked && (
              <Iconography
                category="global"
                color={
                  theme === 'light'
                    ? isDisabled
                      ? 'gray-5'
                      : 'black'
                    : isDisabled
                      ? 'gray-5'
                      : 'white'
                }
                name="circle"
                size={8}
              />
            )}
          </div>
          <Box>
            <Typography variant="body" size="small" className={styles.label} direction={direction}>
              {option?.label}
            </Typography>
            <Typography
              variant="body"
              size="extraSmall"
              className={styles.description}
              direction={direction}
            >
              {option?.description}
            </Typography>
          </Box>
        </div>
      </label>
    </div>
  );
};

RadioButton.displayName = 'RadioButton';

export default RadioButton;
